// src/features/products/components/ProductDetailModal.tsx
import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Divider,
  IconButton,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import { useQueries, useMutation } from "@tanstack/react-query";
import { Product, ProductVariation } from "../types";
import { productsService } from "../services/productService";
import { useCurrency } from "../../../contexts/CurrencyContext";
import { ReactComponent as IconPrev } from "../../../assets/prev.svg";
import { ReactComponent as IconNext } from "../../../assets/next.svg";
import { cartService } from "../../cart/services/cartService";
import { Toast } from "../../../components/common/Toast/Toast";
import { trackAddToCart, trackViewItem } from "../../../services/analytics/analytics";

interface ProductDetailModalProps {
  productId: number | null;
  open: boolean;
  onClose: () => void;
}

export const ProductDetailModal: React.FC<ProductDetailModalProps> = ({
  productId,
  open,
  onClose,
}) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const [overrideImageSrc, setOverrideImageSrc] = useState<string | null>(null);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const navigate = useNavigate();
  const { currency } = useCurrency();
  const currencySymbol = currency === 'THB' ? '฿' : '€';
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });

  const addToCartMutation = useMutation({
    mutationFn: cartService.addToCart,
    onSuccess: () => {
      setToast({ open: true, message: "Added to Shopping Bag", severity: "success" });
    },
    onError: () => {
      setToast({ open: true, message: "Failed to add item. Please try again.", severity: "error" });
    },
  });

  const [productQuery, variationsQuery] = useQueries({
    queries: [
      {
        queryKey: ["product", productId],
        queryFn: () =>
          productsService.getProduct(productId!, {
            _fields: "id,name,price,images,description,stock_status",
          }),
        enabled: open && !!productId,
      },
      {
        queryKey: ["productVariations", productId],
        queryFn: () =>
          productsService.getProductVariations(productId!, {
            _fields:
              "id,name,price,regular_price,sale_price,stock_status,attributes,image",
          }),
        enabled: open && !!productId,
      },
    ],
  });

  const product = productQuery.data as Product | undefined;
  const variations = variationsQuery.data as ProductVariation[] | undefined;
  const isLoading = productQuery.isLoading || variationsQuery.isLoading;
  const error = productQuery.error || variationsQuery.error;

  // ดึง unique sizes และ colors จาก variations
  const uniqueSizes = useMemo(() => {
    if (!variations) return [];
    const sizes = variations
      .flatMap((v) => v.attributes.filter((a) => a.name === "Size"))
      .map((a) => a.option);
    return [...new Set(sizes)];
  }, [variations]);

  const uniqueColors = useMemo(() => {
    if (!variations) return [] as { name: string; imageSrc: string }[];
    const seen = new Set<string>();
    const colors: { name: string; imageSrc: string }[] = [];
    for (const v of variations) {
      const colorAttr = v.attributes.find((a) => a.name === "Color");
      if (colorAttr && !seen.has(colorAttr.option)) {
        seen.add(colorAttr.option);
        colors.push({
          name: colorAttr.option,
          imageSrc: v.image?.src || "",
        });
      }
    }
    return colors;
  }, [variations]);

  const matchedVariation = useMemo(() => {
    if (!variations) return undefined;
    return variations.find((v) => {
      const sizeAttr = v.attributes.find((a) => a.name === "Size");
      const colorAttr = v.attributes.find((a) => a.name === "Color");
      const sizeMatch = !selectedSize || sizeAttr?.option === selectedSize;
      const colorMatch = !selectedColor || colorAttr?.option === selectedColor;
      return sizeMatch && colorMatch;
    });
  }, [variations, selectedSize, selectedColor]);

  // ต้องเลือกครบทุก attribute ที่มีอยู่ก่อนจึงจะกดได้
  const isSelectionComplete =
    (uniqueSizes.length === 0 || selectedSize !== null) &&
    (uniqueColors.length === 0 || selectedColor !== null);

  // ไซส์ถือว่าซื้อไม่ได้ ถ้าไม่มี variation ที่ตรงกับไซส์นี้ (และสีที่เลือกไว้ ถ้ามี) ที่ instock
  const isSizeAvailable = (size: string) => {
    if (!variations) return true;
    const matches = variations.filter((v) => {
      const sizeAttr = v.attributes.find((a) => a.name === "Size");
      const colorAttr = v.attributes.find((a) => a.name === "Color");
      const sizeMatch = sizeAttr?.option === size;
      const colorMatch = !selectedColor || colorAttr?.option === selectedColor;
      return sizeMatch && colorMatch;
    });
    if (matches.length === 0) return true;
    return matches.some((v) => v.stock_status === "instock");
  };

  // สินค้าหมด: ถ้าเลือก variation ครบแล้วให้เช็ค stock ของ variation นั้น ไม่ใช่ของสินค้าหลัก
  const isOutOfStock = matchedVariation
    ? matchedVariation.stock_status !== "instock"
    : product?.stock_status !== "instock";

  // ทุก variation หมดสต๊อกหมดแล้ว (ไม่ว่าจะเลือกไซส์/สีอะไรก็ซื้อไม่ได้)
  const isFullyOutOfStock =
    variations && variations.length > 0
      ? variations.every((v) => v.stock_status !== "instock")
      : product?.stock_status !== "instock";

  const handleAddToCart = () => {
    if (!product || !isSelectionComplete) return;
    if (!localStorage.getItem('token')) {
      onClose();
      navigate('/auth');
      return;
    }
    const attributes: Record<string, string> = {};
    if (matchedVariation) {
      for (const attr of matchedVariation.attributes) {
        attributes[`attribute_${attr.slug}`] = attr.option;
      }
    }
    addToCartMutation.mutate(
      {
        id: product.id,
        ...(matchedVariation ? { variation_id: matchedVariation.id } : {}),
        quantity,
        ...(Object.keys(attributes).length > 0 ? { attributes } : {}),
      },
      {
        onSuccess: () =>
          trackAddToCart(currency, {
            item_id: product.id,
            item_name: product.name,
            price: (matchedVariation?.price ?? product.price)?.[currency] ?? 0,
            quantity,
            item_variant: matchedVariation?.attributes.map((a) => a.option).join(" / "),
          }),
      },
    );
  };

  React.useEffect(() => {
    if (!open || !product) return;
    trackViewItem(currency, {
      item_id: product.id,
      item_name: product.name,
      price: product.price?.[currency] ?? 0,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, product?.id]);

  React.useEffect(() => {
    setSelectedImage(0);
    setOverrideImageSrc(null);
    setSelectedSize(null);
    setSelectedColor(null);
    setQuantity(1);
  }, [productId]);

  const handleClose = (
    event: {},
    reason: "backdropClick" | "escapeKeyDown",
  ) => {
    console.log("🔴 Close triggered by:", reason);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "90vh",
        },
      }}
    >
      {/* ปุ่มปิด */}
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          zIndex: 1,
          bgcolor: "white",
          "&:hover": { bgcolor: "#f5f5f5" },
        }}
      >
        <Close />
      </IconButton>

      <DialogContent sx={{ p: 0 }}>
        {/* Loading State */}
        {isLoading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "400px",
            }}
          >
            <CircularProgress size={60} />
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Box sx={{ p: 4 }}>
            <Alert severity="error">
              Error loading product: {error.message}
            </Alert>
          </Box>
        )}

        {/* Product Content */}
        {!isLoading && !error && product && (
          <>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                padding: { xs: "1.5em", md: "3em" },
              }}
            >
              {/* ฝั่งซ้าย - รูปภาพ */}
              <Box
                sx={{
                  width: { xs: "100%", md: "40%" },
                }}
              >
                {/* รูปใหญ่ */}
                <Box
                  sx={{
                    width: "100%",
                    paddingTop: "100%",
                    position: "relative",
                    overflow: "hidden",
                    "&:hover .nav-btn": {
                      opacity: 1,
                    },
                  }}
                >
                  {product.images && product.images.length > 0 && (
                    <img
                      src={
                        overrideImageSrc ||
                        product.images[selectedImage]?.src ||
                        product.images[0].src
                      }
                      alt={product.name}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  )}

                  {/* Prev / Next Buttons - ซ่อนเมื่อกด color */}
                  {!overrideImageSrc &&
                    product.images &&
                    product.images.length > 1 && (
                      <>
                        <Box
                          className="nav-btn"
                          onClick={() =>
                            setSelectedImage((prev) =>
                              prev === 0 ? product.images.length - 1 : prev - 1,
                            )
                          }
                          sx={{
                            position: "absolute",
                            left: 12,
                            top: "50%",
                            transform: "translateY(-50%)",
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            bgcolor: "rgba(255,255,255,0.85)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            opacity: 0,
                            transition: "opacity 0.3s",
                            "&:hover": { bgcolor: "white" },
                          }}
                        >
                          <IconPrev width={7} height={13} />
                        </Box>
                        <Box
                          className="nav-btn"
                          onClick={() =>
                            setSelectedImage((prev) =>
                              prev === product.images.length - 1 ? 0 : prev + 1,
                            )
                          }
                          sx={{
                            position: "absolute",
                            right: 12,
                            top: "50%",
                            transform: "translateY(-50%)",
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            bgcolor: "rgba(255,255,255,0.85)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            opacity: 0,
                            transition: "opacity 0.3s",
                            "&:hover": { bgcolor: "white" },
                          }}
                        >
                          <IconNext width={7} height={13} />
                        </Box>
                      </>
                    )}

                  {/* Sale Badge */}
                  {product.on_sale && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 16,
                        left: 16,
                        bgcolor: "#f44336",
                        color: "white",
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        fontSize: "14px",
                        fontWeight: 600,
                      }}
                    >
                      SALE
                    </Box>
                  )}
                </Box>

                {/* Thumbnails */}
                {product.images && product.images.length > 1 && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      p: 2,
                      overflowX: "auto",
                    }}
                  >
                    {product.images.map((image, index) => (
                      <Box
                        key={image.id}
                        onClick={() => {
                          setSelectedImage(index);
                          setOverrideImageSrc(null);
                        }}
                        sx={{
                          width: 80,
                          height: 80,
                          flexShrink: 0,
                          border:
                            selectedImage === index
                              ? "2px solid #000"
                              : "2px solid transparent",
                          borderRadius: 1,
                          overflow: "hidden",
                          cursor: "pointer",
                          transition: "all 0.2s",
                          "&:hover": {
                            borderColor: "#666",
                          },
                        }}
                      >
                        <img
                          src={image.src}
                          alt={`${product.name} ${index + 1}`}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              {/* ฝั่งขวา - รายละเอียด */}
              <Box
                sx={{
                  width: { xs: "100%", md: "60%" },
                  p: { xs: 0, md: 4 },
                  pt: { xs: 2, md: 4 },
                }}
              >
                {/* หมวดหมู่ */}
                {product.categories && product.categories.length > 0 && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#666",
                      fontSize: "14px",
                      mb: 1,
                      textTransform: "uppercase",
                      letterSpacing: 1,
                    }}
                  >
                    {product.categories[0].name}
                  </Typography>
                )}

                {/* ชื่อสินค้า */}
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { xs: "24px", md: "28px" },
                    fontFamily: 'Montserrat',
                    fontWeight: 500,
                    mb: 2,
                    lineHeight: 1.3,
                  }}
                >
                  {product.name}
                </Typography>

                {/* รายละเอียด */}
                <Typography
                  variant="h4"
                  component="div"
                  sx={{
                    fontSize: { xs: "14px", md: "18px" },
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.3,
                  }}
                  dangerouslySetInnerHTML={{ __html: product.description }}
                />

                {/* ราคา */}
                <Box sx={{ mb: 3 }}>
                  {(() => {
                    const displayPrice = matchedVariation
                      ? matchedVariation.price
                      : product.price;
                    const displayRegularPrice = matchedVariation
                      ? matchedVariation.regular_price
                      : product.regular_price;
                    const hasSale = matchedVariation
                      ? !!matchedVariation.sale_price &&
                        matchedVariation.sale_price.THB !==
                          matchedVariation.regular_price.THB
                      : product.on_sale;
                    return (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          mb: 1,
                        }}
                      >
                        <Typography
                          variant="h5"
                          sx={{
                            fontSize: "32px",
                            fontWeight: 700,
                            color: hasSale ? "#f44336" : "#000",
                          }}
                        >
                          {currencySymbol}{(displayPrice?.[currency] ?? 0).toLocaleString()}
                        </Typography>

                        {hasSale && displayRegularPrice && (
                          <Typography
                            variant="h6"
                            sx={{
                              fontSize: "24px",
                              color: "#999",
                              textDecoration: "line-through",
                            }}
                          >
                            {currencySymbol}{(displayRegularPrice?.[currency] ?? 0).toLocaleString()}
                          </Typography>
                        )}
                      </Box>
                    );
                  })()}
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Link
                    to={`/product-detail/${product.id}`}
                    style={{
                      color: "#000",
                      textDecoration: "none",
                      fontSize: "14px",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    View full details <IconNext width={7} height={13} />
                  </Link>
                </Box>

                <Divider
                  sx={{ borderWidth: "0.5px", borderColor: "#D9D9D9", mb: 3 }}
                />

                {/* Size */}
                {uniqueSizes.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1.5,
                      }}
                    >
                      <Typography
                        variant="h4"
                        sx={{
                          fontSize: { xs: "16px", md: "18px" },
                          fontWeight: 600,
                          color: "#999",
                        }}
                      >
                        Size
                      </Typography>
                      {/* <Link
                        to="#"
                        style={{
                          color: "#000",
                          textDecoration: "none",
                          fontSize: "14px",
                        }}
                      >
                        Size Chart
                      </Link> */}
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {uniqueSizes.map((size) => {
                        const isSelected = selectedSize === size;
                        const isAvailable = isSizeAvailable(size);
                        return (
                          <Button
                            key={size}
                            variant={isSelected ? "contained" : "outlined"}
                            disabled={!isAvailable}
                            onClick={() =>
                              setSelectedSize(isSelected ? null : size)
                            }
                            sx={{
                              minWidth: "48px",
                              px: 2,
                              py: 1,
                              borderRadius: 1,
                              fontSize: "14px",
                              fontWeight: 600,
                              color: isSelected ? "#fff" : "#333",
                              bgcolor: isSelected ? "#333" : "#f5f5f5",
                              borderColor: isSelected ? "#333" : "#e0e0e0",
                              "&:hover": {
                                bgcolor: isSelected ? "#444" : "#eee",
                                borderColor: "#333",
                              },
                              "&.Mui-disabled": {
                                color: "#bbb",
                                bgcolor: "#f5f5f5",
                                borderColor: "#e0e0e0",
                              },
                            }}
                          >
                            {size}
                          </Button>
                        );
                      })}
                    </Box>
                  </Box>
                )}

                {/* Color */}
                {uniqueColors.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontSize: { xs: "16px", md: "18px" },
                        fontWeight: 600,
                        mb: 1.5,
                        color: "#999",
                      }}
                    >
                      Color
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                      {uniqueColors.map((color) => {
                        const isSelected = selectedColor === color.name;
                        return (
                          <Box
                            key={color.name}
                            onClick={() => {
                              setSelectedColor(isSelected ? null : color.name);
                              setOverrideImageSrc(
                                !isSelected && color.imageSrc
                                  ? color.imageSrc
                                  : null,
                              );
                            }}
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: "2%",
                              overflow: "hidden",
                              cursor: "pointer",
                              border: isSelected
                                ? "3px solid #333"
                                : "2px solid #e0e0e0",
                              transition: "all 0.2s",
                              "&:hover": {
                                borderColor: "#333",
                              },
                            }}
                          >
                            {color.imageSrc ? (
                              <img
                                src={color.imageSrc}
                                alt={color.name}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: "100%",
                                  height: "100%",
                                  bgcolor: "#ccc",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "10px",
                                }}
                              >
                                {color.name}
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Actions - อยู่ล่างสุด */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: { xs: "flex-start", sm: "right" },
                alignItems: { xs: "flex-start", sm: "center" },
                gap: 2,
                p: { xs: 2, md: 3 },
              }}
            >
              {/* Quantity Selector */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography
                  variant="body1"
                  sx={{
                    fontSize: { xs: "16px", md: "18px" },
                    fontWeight: 400,
                  }}
                >
                  Quantity :
                </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  height: "48px",
                }}
              >
                <IconButton
                  onClick={() => setQuantity((prev) => Math.max(1, prev - 1))}
                  disabled={quantity <= 1}
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 0,
                    color: "#333",
                    "&:disabled": { color: "#ccc" },
                  }}
                >
                  <Typography sx={{ fontSize: "20px", fontWeight: 600 }}>
                    −
                  </Typography>
                </IconButton>
                <Typography
                  sx={{
                    width: 48,
                    textAlign: "center",
                    fontSize: "16px",
                    fontWeight: 600,
                    userSelect: "none",
                  }}
                >
                  {quantity}
                </Typography>
                <IconButton
                  onClick={() => setQuantity((prev) => prev + 1)}
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 0,
                    color: "#333",
                  }}
                >
                  <Typography sx={{ fontSize: "20px", fontWeight: 600 }}>
                    +
                  </Typography>
                </IconButton>
              </Box>
              </Box>

              <Button
                variant="contained"
                size="large"
                disabled={
                isOutOfStock ||
                !isSelectionComplete ||
                addToCartMutation.isPending
              }
                sx={{
                  bgcolor: "#1A1A1A",
                  color: "#FFFFFF",
                  width: { xs: "100%", sm: "309px" },
                  height: "48px",
                  py: 1.5,
                  "&:hover": { bgcolor: "#333" },
                  "&:disabled": { bgcolor: "#ccc" },
                }}
                onClick={handleAddToCart}
              >
                {isFullyOutOfStock
                  ? "Out of Stock"
                  : addToCartMutation.isPending
                  ? "Adding..."
                  : "Add to Shopping Bag"}
              </Button>
            </Box>
          </>
        )}
      </DialogContent>
      <Toast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
      />
    </Dialog>
  );
};
